/**
 *  @file       dcp-localstorage.js
 *  @author     Ryan Rossiter, ryan@kingsds.network
 *              Wes Garland, wes@distributive.network
 *  @date       March 2020, Sep 2022
 *
 * This module is used to provide a uniform localStorage
 * interface on node and the browser. In node it uses the
 * node-localstorage npm package, and in the browser it uses
 * the built-in localstorage API.
 */
'use strict';

const debugging = require('dcp/debugging').scope('localstorage');
const dcpEnv = require('./dcp-env');

/** Allow for memory-only localstorage if env var is set. Used mainly for a few tests to have workers with new workerIds */
if (dcpEnv.getenv('DCP_LOCALSTORAGE_PRIVATE'))
{
  let data = {};
  exports.localStorage = {
    length: Object.keys(data).length,
    key: (n) => Object.keys(data)[n] || null,
    getItem: (key) => data[key],
    setItem: (key, value) => { data[key] = value },
    removeItem: (key) => { delete data[key] },
    clear: () => { data = {} },
  };
}
/** Fall back to host environment's implementation when not nodejs */
else if (dcpEnv.isBrowserPlatform)
{
  exports.localStorage = (globalThis || window || self).localStorage;

  if (typeof exports.localStorage === 'undefined' || exports.localStorage === null)
    throw new Error(`Host environment is ${require('./dcp-env').platform} but localStorage is not defined!`);
}
else
{
  const fsBasic = require('./fs-basic');
  const hash    = require('./hash');
  const applicationStorageId = dcpEnv.getenv('DCP_APPLICATION_STORAGE_ID') || (dcpEnv.programFilename ? hash.calculate(hash.eh1, dcpEnv.programFilename) : 'default');

  // These are the properties and methods of localStorage
  // that would do nothing if there was no localStorage data
  const emptyLocalStorageStub = {
    length: 0,
    key: () => null,
    getItem: () => null,
    removeItem: () => {},
    clear: () => {},
  }

  /** 
   * Deferring this calculation as late as possible lets us change the homedir after loading dcp-client,
   * but before reading/writing to local storage.
   */
  function calculateLocalStoragePath()
  {
    return fsBasic.joinPath(require('dcp/utils/resource-dir').getResourceDir(), 'localStorage', applicationStorageId);
  }

  /**
   * Create disk storage. This is deferred as late as possible to avoid
   * writing to the hard disk just to check to see if a property in an
   * empty object exists.
   */
  function createBackingStore()
  {
    const { LocalStorage } = require('./disk-based-localstorage');

    if (!fsBasic.dirExists(exports.localStoragePath))
    {
      debugging() && console.debug("Creating localStorage path:", exports.localStoragePath);
      fsBasic.mkdir(exports.localStoragePath, { recursive: true });
    }

    return new LocalStorage(exports.localStoragePath);
  }

  var backingStore;
  /**
   * Strategy: localStorage proxy in front of the node-localstorage::LocalStorage object, so that
   * can avoid actually creating the local storage until it is truly necesary.
   */
  exports.localStorage = new Proxy({}, {
    get(target, prop)
    {
      debugging() && console.debug(`localStorage: resolving ${prop}`);
      
      /* fast-path - already instanciated backing store */
      if (backingStore && prop !== 'getPath')
        return backingStore[prop].bind(backingStore);

      if (!exports.localStoragePath)
      {
        exports.localStoragePath = calculateLocalStoragePath();
        if (fsBasic.dirExists(exports.localStoragePath))
        {
          /* Storage already on disk => intantiate immediately. */
          backingStore = createBackingStore();
          if (prop in backingStore)
            return backingStore[prop].bind(backingStore);
        }

        const preload = dcpEnv.getenv('DCP_PRELOAD_LOCALSTORAGE');
        if (preload)
        {
          const preloadObj = JSON.parse(preload);
          for (let key in preloadObj)
            exports.localStorage.setItem(key, preloadObj[value]);
        }
      }

      if (emptyLocalStorageStub.hasOwnProperty(prop))
        return emptyLocalStorageStub[prop];

      if (prop === 'getPath') /* debug helper, not API */
      {
        return function localStorage$$getPath() {
          return exports.localStoragePath;
        };
      }

      /* real storage does not exist, prop not in stubs => this is a method we can't fake,
       * so we must instantiate the real deal
       */
      backingStore = createBackingStore();
      return backingStore[prop].bind(backingStore);
    }
  });

  exports.localStoragePath = false;
}
